import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ElectricBoltOutlined, SwapVert } from '@mui/icons-material';
import SocGauge from './SocGauge';
import NumberLabel from './NumberLabel';
import DetailLabel from './DetailLabel';
import HourlyPlot from './HourlyPlot';
import DailyPlot from './DailyPlot';
import PlotModeToggle from '../PlotModeToggle';

const StringCard = ({
  id,
  label,
  data
}) => {

  const [tsHourlySoc, setTsHourlySoc] = useState(null);
  const [tsHourlyTemp, setTsHourlyTemp] = useState(null);


  const [tsDailySocMin, setTsDailySocMin] = useState(null);
  const [tsDailySocMax, setTsDailySocMax] = useState(null);
  const [tsDailySocUse, setTsDailySocUse] = useState(null);
  const [tsDailyTempMin, setTsDailyTempMin] = useState(null);
  const [tsDailyTempMax, setTsDailyTempMax] = useState(null);

  const [plotMode, setPlotMode] = useState("hourly");

  const processHourlyData = (data) => {
    let _tsHourlySoc = {
      x: [],
      y: [],
      name: "SOC",
      type: "bar",
      marker: { color: "#3B82F6", cornerradius: 3 },
    };
    let _tsHourlyTemp = {
      x: [],
      y: [],
      name: "Temperature",
      line: { color: "#F97316", width: 2.5 },
      type: "scatter",
      mode: "lines",
      yaxis: "y2",
    };

    Object.entries(data).map(([epoch, epochData]) => {
      // console.log(epochData);
      if (epochData.local_datetime_str) {
        if (epochData.SOC != null) {
          _tsHourlySoc.x.push(epochData.local_datetime_str);
          _tsHourlySoc.y.push(epochData.SOC);
        }
        if (epochData.T_cell_max != null) {
          _tsHourlyTemp.x.push(epochData.local_datetime_str);
          _tsHourlyTemp.y.push(epochData.T_cell_max);
        }
      }
    });

    setTsHourlySoc(_tsHourlySoc);
    setTsHourlyTemp(_tsHourlyTemp);
  }

  const processDailyData = (data) => {
    let _tsDailySocMin = {
      x: [],
      y: [],
      name: "Min SOC",
      type: "bar",
      marker: { color: "#E2E8F0" },
    };
    let _tsDailySocMax = {
      x: [],
      y: [],
      name: "Max SOC",
      type: "bar",
      marker: { color: "#3B82F6" },
    };
    let _tsDailySocUse = {
      x: [],
      y: [],
      name: "Utilized SOC",
      type: "bar",
      marker: { color: "#3B82F6", cornerradius: 3 },
    };
    let _tsDailyTempMin = {
      x: [],
      y: [],
      name: "Min temp.",
      line: { color: "#0EA5E9", width: 2 },
      type: "scatter",
      mode: "lines",
      yaxis: "y2",
    };
    let _tsDailyTempMax = {
      x: [],
      y: [],
      name: "Max temp.",
      line: { color: "#EF4444", width: 2 },
      type: "scatter",
      mode: "lines",
      yaxis: "y2",
    };

    Object.entries(data).map(([epoch, epochData]) => {
      if (epochData.local_datetime_str) {
        let _socMin = 0;
        let _socMax = 0;
        let _socUse = 0;
        if (epochData.SOC_min) {
          _socMin = epochData.SOC_min;
          _tsDailySocMin.x.push(epochData.local_datetime_str);
          _tsDailySocMin.y.push(epochData.SOC_min);
        }
        if (epochData.SOC_max) {
          _socMax = epochData.SOC_max;
          _tsDailySocMax.x.push(epochData.local_datetime_str);
          _tsDailySocMax.y.push(epochData.SOC_max);
        }
        if (epochData.SOC_min && epochData.SOC_max) {
          _socUse = _socMax - _socMin;
        }
        _tsDailySocUse.x.push(epochData.local_datetime_str);
        _tsDailySocUse.y.push(_socUse);


        if (epochData.T_cell_min) {
          _socMin = epochData.SOC_min;
          _tsDailyTempMin.x.push(epochData.local_datetime_str);
          _tsDailyTempMin.y.push(epochData.T_cell_min);
        }
        if (epochData.T_cell_max) {
          _socMin = epochData.SOC_min;
          _tsDailyTempMax.x.push(epochData.local_datetime_str);
          _tsDailyTempMax.y.push(epochData.T_cell_max);
        }

      }
    });

    setTsDailySocMax(_tsDailySocMax);
    setTsDailySocMin(_tsDailySocMin);
    setTsDailySocUse(_tsDailySocUse);
    setTsDailyTempMax(_tsDailyTempMax);
    setTsDailyTempMin(_tsDailyTempMin);
  }

  useEffect(() => {
    // console.log(data);

    // Hourly time series
    if (data != null && data.string_data_ts_hour != null) {
      processHourlyData(data.string_data_ts_hour);
    } else {
      setTsHourlySoc(null);
      setTsHourlyTemp(null);
    }

    // Daily time series
    if (data != null && data.string_data_ts_day != null) {
      processDailyData(data.string_data_ts_day);
    } else {
      setTsDailySocMax(null);
      setTsDailySocMin(null);
      setTsDailySocUse(null);
      setTsDailyTempMax(null);
      setTsDailyTempMin(null);
    }

  }, [data]);

  const latest = data.string_data_latest || {};

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {/* String label, only needed when a block has several strings */}
      {label && (
        <Typography sx={{ px: 2.5, pt: 2, fontSize: 13, fontWeight: 600, color: "var(--c-text-2)" }}>
          {label}
        </Typography>
      )}

      {/* Data box */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "200px 160px minmax(0, 1fr)" },
        alignItems: "center",
        gap: 3,
        p: 2.5,
      }}>
        {/* SOC gauge */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {latest.SOC != null && (
            <SocGauge
              soc={latest.SOC}
              statusText={latest.state}
            />
          )}
        </Box>

        {/* Data labels */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "1fr" },
          gap: 1.5,
        }}>
          {latest.V_total != null &&
            <NumberLabel
              label="Voltage"
              icon={<ElectricBoltOutlined />}
              value={latest.V_total}
              unit="V"
            />
          }
          {latest.I_total != null &&
            <NumberLabel
              label="Current"
              icon={<SwapVert />}
              value={latest.I_total}
              unit="A"
            />
          }
        </Box>

        {/* Plot */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
          {/* Plot mode selector */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 500, color: "var(--c-text-muted)" }}>
              {plotMode === "hourly" ? "SOC and temperature, hourly" : "SOC range and temperature, daily"}
            </Typography>
            <PlotModeToggle value={plotMode} onChange={setPlotMode} />
          </Box>

          {/* Actual plots */}
          <Box sx={{ height: 220 }}>
            {plotMode === "hourly"
              && tsHourlySoc != null
              && tsHourlyTemp != null
              &&
              <HourlyPlot
                tsSoc={tsHourlySoc}
                tsTemperature={tsHourlyTemp}
              />
            }

            {plotMode === "daily"
              && tsDailySocMin != null
              && tsDailySocUse != null
              && tsDailyTempMin != null
              && tsDailyTempMax != null
              &&
              <DailyPlot
                tsSocMin={tsDailySocMin}
                tsSocMax={tsDailySocMax}
                tsSocUse={tsDailySocUse}
                tsTemperatureMin={tsDailyTempMin}
                tsTemperatureMax={tsDailyTempMax}
              />
            }
          </Box>
        </Box>
      </Box>

      {/* Details box */}
      <Box sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        px: 2.5,
        py: 1.5,
        borderTop: "1px solid var(--c-muted)",
        backgroundColor: "var(--c-subtle)",
      }}>
        {latest.SOH != null &&
          <DetailLabel label="SOH" value={latest.SOH} unit="%" />
        }
        {latest.min_cell_voltage != null &&
          <DetailLabel label="Min cell voltage" value={latest.min_cell_voltage} unit="V" />
        }
        {latest.max_cell_voltage != null &&
          <DetailLabel label="Max cell voltage" value={latest.max_cell_voltage} unit="V" />
        }
        {latest.min_cell_temperature != null &&
          <DetailLabel label="Min temperature" value={latest.min_cell_temperature} unit="°C" />
        }
        {latest.max_cell_temperature != null &&
          <DetailLabel label="Max temperature" value={latest.max_cell_temperature} unit="°C" />
        }
      </Box>
    </Box>
  )
}

export default StringCard;
